import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  CardGiftcard as CardGiftcardIcon,
  Settings as SettingsIcon,
  LocalActivity as LocalActivityIcon,
  Store as StoreIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  Tune as TuneIcon,
  LocalAtm
} from '@mui/icons-material';

// Import admin components
import TokenStatistics from '../../../components/skillcoin/admin/TokenStatistics';
import EarningActivities from '../../../components/skillcoin/admin/EarningActivities';
import TokenDisbursement from '../../../components/skillcoin/admin/TokenDisbursement';
import TokenSettings from '../../../components/skillcoin/admin/TokenSettings';
import TokenSettingsModal from '../../../components/skillcoin/admin/TokenSettingsModal';
import AdminMarketplaceManager from '../../../components/skillcoin/admin/AdminMarketplaceManager';
import { tenantTokenService } from '../../../services/skillcoinService';

const AdminSkillCoinDashboardPage = () => {
  const { tab = 'dashboard' } = useParams();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(tab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [earningActivities, setEarningActivities] = useState([]);
  const [serviceAvailable, setServiceAvailable] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  
  useEffect(() => {
    fetchTokenData();
  }, []);
  
  useEffect(() => {
    // Update the tab state if the URL parameter changes
    setCurrentTab(tab);
    
    // Only load tab-specific data if service is available and token exists
    if (token && serviceAvailable) {
      switch (tab) {
        case 'statistics':
          fetchStatistics();
          break;
        case 'activities':
          fetchEarningActivities();
          break;
        default:
          break;
      }
    }
  }, [tab, token, serviceAvailable]);

  const fetchTokenData = async () => {
    try {
      setLoading(true);
      console.log('Fetching token data...');
      const response = await tenantTokenService.getTenantToken();
      console.log('Token data received:', response);
      
      // Handle different response formats
      let tokenData = null;
      if (response && response.success === true && response.data) {
        // Format: { success: true, data: {...} }
        tokenData = response.data;
      } else if (response && response.token) {
        // Format: { token: {...} }
        tokenData = response.token;
      } else if (response && !response.success && !response.data && !response.token) {
        // Assume response is the token object directly
        tokenData = response;
      }
      
      console.log('Processed token data:', tokenData);
      setToken(tokenData);
      setServiceAvailable(true);
      
      if (tokenData) {
        // Load initial data based on current tab
        switch (currentTab) {
          case 'statistics':
            fetchStatistics();
            break;
          case 'activities':
            fetchEarningActivities();
            break;
          default:
            break;
        }
      }
    } catch (err) {
      console.error('Error fetching token data:', err);
      
      // Check if it's a 404 error (API endpoint not found)
      if (err.response && err.response.status === 404) {
        setServiceAvailable(false);
        setError('SkillCoin service is not available. The backend API endpoint may not be implemented yet.');
      } else {
        setError('Failed to load token data. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const stats = await tenantTokenService.getTokenStatistics('month');
      setStatistics(stats);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Failed to load statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEarningActivities = async () => {
    try {
      setLoading(true);
      const activities = await tenantTokenService.getEarningActivities();
      setEarningActivities(activities);
    } catch (err) {
      console.error('Error fetching earning activities:', err);
      setError('Failed to load earning activities. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    const tabMapping = {
      0: 'dashboard',
      1: 'statistics',
      2: 'activities',
      3: 'disbursement',
      4: 'settings',
      5: 'marketplace',
      6: 'modal'
    };
    
    const newTab = tabMapping[newValue] || 'dashboard';
    
    if (newTab === 'modal') {
      setModalOpen(true);
      return;
    }
    
    setCurrentTab(newTab);
    navigate(`/admin/skillcoin/${newTab}`);
  };
  
  // Map tab name to numeric index for Tabs component
  const getTabIndex = (tabName) => {
    const tabMapping = {
      'dashboard': 0,
      'statistics': 1,
      'activities': 2,
      'disbursement': 3,
      'settings': 4,
      'marketplace': 5,
      'modal': 6
    };
    
    return tabMapping[tabName] || 0;
  };

  const handleCreateToken = async () => {
    try {
      if (!serviceAvailable) {
        setError('Cannot create token: SkillCoin service is not available. Please ensure the backend API is implemented.');
        return;
      }
      
      setLoading(true);
      setError(null);
      
      const defaultToken = {
        name: 'MembaCoin',
        symbol: 'MBC',
        description: 'Community token for rewarding member contributions and participation',
        baseRate: 0.01,
        isExchangeable: true,
        isTransferable: true,
        maxSupply: 0
      };
      
      await tenantTokenService.createToken(defaultToken);
      await fetchTokenData();
    } catch (err) {
      console.error('Error creating token:', err);
      
      if (err.response && err.response.status === 404) {
        setServiceAvailable(false);
        setError('Cannot create token: SkillCoin service is not available. The backend API endpoint may not be implemented yet.');
      } else {
        setError('Failed to create token. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTokenSettings = async (updatedToken) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Saving token settings with data:', updatedToken);
      
      if (!token) {
        // Creating a new token
        const response = await tenantTokenService.createToken(updatedToken);
        console.log('Token created:', response);
      } else {
        // Updating existing token
        const response = await tenantTokenService.updateToken(updatedToken);
        console.log('Token updated:', response);
      }
      
      await fetchTokenData();
      setModalOpen(false);
    } catch (err) {
      console.error('Error saving token settings:', err);
      setError('Failed to save token settings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Token creation card for tabs that require a token
  const TokenCreationCard = () => (
    <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Token System Not Configured
      </Typography>
      <Typography color="textSecondary" paragraph>
        Your community doesn't have a token system set up yet. Create one to access this feature.
      </Typography>
      
      <Box mt={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={handleCreateToken}
          disabled={loading || !serviceAvailable}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Token System'}
        </Button>
      </Box>
    </Paper>
  );

  // Service unavailable message
  const ServiceUnavailableMessage = () => (
    <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Box display="flex" justifyContent="center" mb={2}>
        <ErrorIcon color="error" sx={{ fontSize: 48 }} />
      </Box>
      <Typography variant="h5" gutterBottom color="error">
        SkillCoin Service Unavailable
      </Typography>
      <Typography color="textSecondary" paragraph>
        The SkillCoin service API appears to be unavailable or not implemented yet. 
        The required backend endpoints could not be reached.
      </Typography>
      <Typography color="textSecondary" paragraph>
        Please contact your system administrator or developer to ensure the SkillCoin 
        backend services are properly configured.
      </Typography>
      
      <Box mt={3}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={() => {
            setServiceAvailable(true);
            fetchTokenData();
          }}
        >
          Retry Connection
        </Button>
      </Box>
    </Paper>
  );

  if (loading && !token && currentTab !== 'marketplace') {
    return (
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 1, md: 4 }, 
          mb: 4,
          px: { xs: 2, md: 3 } // Custom padding that's smaller than default
        }}
      >
        <Box mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Token Economy Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your community token economy, marketplace, and reward system
          </Typography>
        </Box>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={getTabIndex(currentTab)}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<DashboardIcon />} label="Overview" />
            <Tab icon={<AssessmentIcon />} label="Statistics" />
            <Tab icon={<LocalActivityIcon />} label="Activities" />
            <Tab icon={<CardGiftcardIcon />} label="Disbursement" />
            <Tab icon={<SettingsIcon />} label="Settings" />
            <Tab icon={<StoreIcon />} label="Marketplace" />
            <Tab icon={<TuneIcon />} label="Token Settings" />
          </Tabs>
        </Paper>

        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: { xs: 1, md: 4 }, 
        mb: 4,
        px: { xs: 2, md: 3 } // Custom padding that's smaller than default
      }}
    >
      <Box mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          {token && token.name && token.symbol 
            ? `${token.name} (${token.symbol}) Management` 
            : 'Token Economy Management'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your community token economy, marketplace, and reward system
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={getTabIndex(currentTab)}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<DashboardIcon />} label="Overview" />
          <Tab icon={<AssessmentIcon />} label="Statistics" />
          <Tab icon={<LocalActivityIcon />} label="Activities" />
          <Tab icon={<CardGiftcardIcon />} label="Disbursement" />
          <Tab icon={<SettingsIcon />} label="Settings" />
          <Tab icon={<StoreIcon />} label="Marketplace" />
          <Tab icon={<TuneIcon />} label="Token Settings" />
        </Tabs>
      </Paper>

      <Box>
        {!serviceAvailable && currentTab !== 'marketplace' ? (
          <ServiceUnavailableMessage />
        ) : (
          <>
            {currentTab === 'dashboard' && (
              !token ? <TokenCreationCard /> : (
                <Box>
                  <Typography variant="h6" gutterBottom>Token Dashboard Overview</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    {/* Token Information Card */}
                    <Paper sx={{ p: 3, position: 'relative', overflow: 'hidden' }}>
                      {/* Token symbol watermark */}
                      <Typography 
                        sx={{ 
                          position: 'absolute', 
                          right: -20, 
                          bottom: -30, 
                          fontSize: '120px', 
                          fontWeight: 'bold', 
                          opacity: 0.05, 
                          color: 'primary.main',
                          zIndex: 0
                        }}
                      >
                        {token.symbol}
                      </Typography>
                      <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocalAtm sx={{ mr: 1, fontSize: 20 }} />
                          Token Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Name:</Typography>
                            <Typography variant="body2" fontWeight="medium">{token.name}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Symbol:</Typography>
                            <Chip 
                              label={token.symbol} 
                              size="small" 
                              color="primary" 
                              variant="outlined" 
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Base Rate:</Typography>
                            <Typography variant="body2" fontWeight="medium">{token.baseRate}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Transferable:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {token.isTransferable ? 
                                <Chip size="small" color="success" label="Yes" /> : 
                                <Chip size="small" color="error" label="No" />}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Exchangeable:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {token.isExchangeable ? 
                                <Chip size="small" color="success" label="Yes" /> : 
                                <Chip size="small" color="error" label="No" />}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Status:</Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ 
                              color: token.isActive ? 'success.main' : 'error.main' 
                            }}>
                              {token.isActive ? 'Active' : 'Inactive'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Created:</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {new Date(token.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                          {token.description && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>Description:</Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>"{token.description}"</Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            startIcon={<TuneIcon />} 
                            onClick={() => setModalOpen(true)}
                          >
                            Edit Token
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                    
                    {/* Token Statistics Quick View */}
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <AssessmentIcon sx={{ mr: 1, fontSize: 20 }} />
                        Token Statistics
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Total Supply:</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {token.totalSupply?.toLocaleString() || '0'} {token.symbol}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Max Supply:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {token.maxSupply ? token.maxSupply.toLocaleString() : 'Unlimited'} {token.symbol}
                          </Typography>
                        </Box>
                        
                        {/* Supply progress indicator */}
                        {token.maxSupply > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">Supply Usage:</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {Math.round((token.totalSupply / token.maxSupply) * 100)}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={(token.totalSupply / token.maxSupply) * 100}
                              sx={{ height: 8, borderRadius: 1 }}
                            />
                          </Box>
                        )}
                        
                        {/* Additional statistics if available */}
                        {statistics && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Total Spent:</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {statistics.totalSpent?.toLocaleString() || '0'} {token.symbol}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Active Users:</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {statistics.activeUsers?.toLocaleString() || '0'}
                              </Typography>
                            </Box>
                          </>
                        )}
                        
                        {/* Link to token statistics page for more details */}
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button 
                            variant="outlined" 
                            size="small"
                            startIcon={<AssessmentIcon />}
                            onClick={() => navigate('/admin/skillcoin/statistics')}
                          >
                            View Detailed Statistics
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                    
                    {/* Activities Quick View */}
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Earning Activities
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      {token.earningActivities && token.earningActivities.length > 0 ? (
                        <>
                          <Typography variant="body2" gutterBottom>
                            Top earning activities:
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {token.earningActivities.slice(0, 3).map((activity, index) => (
                              <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">{activity.name}</Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {activity.tokenAmount} {token.symbol}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No earning activities configured yet
                        </Typography>
                      )}
                      
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => navigate('/admin/skillcoin/activities')}
                        >
                          Manage Activities
                        </Button>
                      </Box>
                    </Paper>
                    
                    {/* Usage Options Quick View */}
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Token Usage Options
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      {token.usageOptions && token.usageOptions.length > 0 ? (
                        <>
                          <Typography variant="body2" gutterBottom>
                            Top usage options:
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {token.usageOptions.slice(0, 3).map((option, index) => (
                              <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">{option.name}</Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {option.tokenAmount} {token.symbol}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No usage options configured yet
                        </Typography>
                      )}
                      
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => navigate('/admin/skillcoin/settings')}
                        >
                          Manage Usage Options
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                  
                  {/* Quick Actions */}
                  <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Quick Actions
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Button 
                        variant="contained" 
                        color="primary"
                        onClick={() => navigate('/admin/skillcoin/disbursement')}
                        startIcon={<CardGiftcardIcon />}
                      >
                        Disburse Tokens
                      </Button>
                      <Button 
                        variant="contained" 
                        color="secondary"
                        onClick={() => navigate('/admin/skillcoin/activities')}
                        startIcon={<LocalActivityIcon />}
                      >
                        Add Earning Activity
                      </Button>
                      <Button 
                        variant="contained" 
                        color="info"
                        onClick={() => navigate('/admin/skillcoin/marketplace')}
                        startIcon={<StoreIcon />}
                      >
                        Manage Marketplace
                      </Button>
                      <Button 
                        variant="outlined"
                        onClick={() => setModalOpen(true)}
                        startIcon={<TuneIcon />}
                      >
                        Edit Token Settings
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              )
            )}
            
            {currentTab === 'statistics' && (
              !token ? <TokenCreationCard /> : (
                <Box>
                  <Typography variant="h6" gutterBottom>Token Economy Statistics</Typography>
                  <TokenStatistics tokenId={token._id} />
                </Box>
              )
            )}
            
            {currentTab === 'activities' && (
              !token ? <TokenCreationCard /> : (
                <Box>
                  <Typography variant="h6" gutterBottom>Earning Activities</Typography>
                  <EarningActivities 
                    activities={earningActivities} 
                    onUpdate={fetchEarningActivities} 
                  />
                </Box>
              )
            )}
            
            {currentTab === 'disbursement' && (
              !token ? <TokenCreationCard /> : (
                <Box>
                  <Typography variant="h6" gutterBottom>Token Disbursement</Typography>
                  <TokenDisbursement 
                    tokenId={token._id} 
                    tokenSymbol={token.symbol}
                    onDisbursement={fetchTokenData}
                  />
                </Box>
              )
            )}
            
            {currentTab === 'settings' && (
              !token ? <TokenCreationCard /> : (
                <Box>
                  <Typography variant="h6" gutterBottom>Token Settings</Typography>
                  <TokenSettings 
                    token={token} 
                    onUpdate={fetchTokenData} 
                  />
                </Box>
              )
            )}
            
            {/* Token Settings Modal */}
            <TokenSettingsModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              onSave={handleSaveTokenSettings}
              token={token}
              isCreating={!token}
            />
          </>
        )}
        
        {/* Marketplace is always shown, regardless of token existence */}
        {currentTab === 'marketplace' && (
          <AdminMarketplaceManager />
        )}
      </Box>
    </Container>
  );
};

export default AdminSkillCoinDashboardPage;
